import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";

// Interface for typing user info
export interface TypingUser {
  user_id: string;
  is_typing: boolean;
}

/**
 * Set the current user's typing status in a conversation
 * @param conversationId The ID of the conversation
 * @param userId The ID of the user
 * @param isTyping Whether the user is currently typing
 */
export const setTypingStatus = async (
  conversationId: string,
  userId: string,
  isTyping: boolean
): Promise<boolean> => {
  const context: ErrorContext = {
    operation: "setTypingStatus",
    additionalInfo: { conversationId, userId },
  };

  try {
    // Use raw SQL query to update or insert typing status
    const { error } = await supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      
      return fetch(`${SUPABASE_URL}/rest/v1/rpc/set_typing_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          p_conversation_id: conversationId,
          p_user_id: userId,
          p_is_typing: isTyping
        })
      });
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData };
      }
      return { error: null };
    });

    if (error) throw error;
    return true;
  } catch (error) {
    handleError(
      error,
      "Failed to set typing status",
      ErrorSeverity.WARNING,
      context
    );
    return false;
  }
};

/**
 * Subscribe to typing status changes in a conversation
 * @param conversationId The ID of the conversation to watch
 * @param callback Function to call when typing status changes
 */
export const subscribeToTypingIndicators = (
  conversationId: string,
  callback: (typingUsers: TypingUser[]) => void
) => {
  // Subscribe to real-time changes in the typing_indicators table
  const channel = supabase
    .channel(`typing-${conversationId}`)
    .on('postgres_changes', 
      { 
        event: '*', 
        schema: 'public', 
        table: 'typing_indicators', 
        filter: `conversation_id=eq.${conversationId}` 
      },
      async () => {
        try {
          // Use raw SQL query to get active typing users
          const { data, error } = await supabase.auth.getSession().then(({ data }) => {
            const token = data.session?.access_token;
            
            return fetch(`${SUPABASE_URL}/rest/v1/rpc/get_active_typing_users`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'apikey': SUPABASE_ANON_KEY,
              },
              body: JSON.stringify({
                p_conversation_id: conversationId,
                p_seconds_threshold: 10
              })
            });
          }).then(async (response) => {
            if (!response.ok) {
              const errorData = await response.json();
              return { error: errorData, data: null };
            }
            const responseData = await response.json();
            return { error: null, data: responseData };
          });
          
          if (error) throw error;
          if (data) {
            // Ensure the data is properly typed
            const typingUsers: TypingUser[] = Array.isArray(data) ? data.map(user => ({
              user_id: user.user_id,
              is_typing: user.is_typing
            })) : [];
            
            callback(typingUsers);
          }
        } catch (err) {
          console.error('Error fetching typing indicators:', err);
        }
      }
    )
    .subscribe();

  return channel;
};

/**
 * Debounced function to update typing status
 * Use this to avoid too many database updates
 */
export const createDebouncedTypingIndicator = (
  conversationId: string,
  userId: string,
  delay = 2000
) => {
  let typingTimeout: NodeJS.Timeout | null = null;
  let isCurrentlyTyping = false;

  return () => {
    // If not currently marked as typing, mark as typing immediately
    if (!isCurrentlyTyping) {
      isCurrentlyTyping = true;
      setTypingStatus(conversationId, userId, true);
    }

    // Clear any existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set a new timeout to clear typing status after delay
    typingTimeout = setTimeout(() => {
      isCurrentlyTyping = false;
      setTypingStatus(conversationId, userId, false);
    }, delay);
  };
}; 